import { useAuth, useFunctions, useSigninCheck } from 'reactfire'
import { useTranslation } from 'react-i18next'
import { Alert, Button, theme } from 'antd'
import { signInWithCustomToken, signOut } from 'firebase/auth'
import { UserSwitchOutlined } from '@ant-design/icons'
import { errorResponse } from './lib/errorResponse'
import { appVersion } from './lib/general'
import { useEffect, useRef, useState } from 'react'
import { api } from './lib/api'
import * as Sentry from '@sentry/react'

export function ImpersonationBar() {
  const auth = useAuth()
  const { t } = useTranslation()
  const { token } = theme.useToken()

  const { data: signInCheck } = useSigninCheck({
    requiredClaims: { impersonating: true },
  })

  const functions = useFunctions()

  const [loading, setLoading] = useState(false)

  async function endImpersonation() {
    try {
      setLoading(true)
      const data = await api(functions, 'impersonate/stop')
      const customToken = data?.customToken
      if (customToken) {
        try {
          await signInWithCustomToken(auth, customToken)
          window.location.replace(returnPath)
        } catch (_e) {
          // Old token expired, so we need to sign in again
          Sentry.setUser(null)
          await signOut(auth)
          window.location.replace('/login')
        }
      } else {
        Sentry.setUser(null)
        await signOut(auth)
        window.location.replace('/login')
      }
    } catch (e) {
      errorResponse(e)
    } finally {
      setLoading(false)
    }
  }

  const [impersonatorName, setImpersonatorName] = useState(null)
  const [returnPath, setReturnPath] = useState('/admin')

  useEffect(() => {
    async function getName() {
      const token = await signInCheck.user.getIdTokenResult()
      setImpersonatorName(token.claims.impersonatorName)
      // Prefer the dedicated exit path: for impersonations started from the
      // Pulse client view, returnPath is an operator-only operations route
      // that the restored impersonator may not read (403). Older tokens only
      // carry returnPath, so keep it as the fallback.
      if (token.claims.exitPath) {
        setReturnPath(token.claims.exitPath)
      } else if (token.claims.returnPath) {
        setReturnPath(token.claims.returnPath)
      }
    }

    // noinspection JSIgnoredPromiseFromCall
    getName()
  }, [signInCheck.user])

  const barRef = useRef(null)

  // This bar is `position: fixed` at the viewport's bottom edge, so it
  // overlays whatever the page would otherwise render there — fine on a
  // short page with natural whitespace below its content, but not on a
  // page whose content runs right up to (or past) the viewport's natural
  // height: there's nothing to push that last bit of content above the
  // bar, and no extra scroll room to reveal it either. The Absences
  // calendar's pagination row did exactly this, sitting permanently
  // hidden underneath the bar while impersonating. Reserve exactly the
  // bar's own rendered height as page bottom padding — rather than a
  // guessed fixed pixel value — so this keeps working if the bar's content
  // (and so its height) ever changes, e.g. text wrapping on narrow screens.
  useEffect(() => {
    if (!signInCheck.hasRequiredClaims) return
    const el = barRef.current?.nativeElement
    if (!el) return

    function applyPadding() {
      // Locally the bar sits 20px above the viewport's bottom edge (see
      // `bottom` below) to stay clear of the emulator-mode banner — that
      // gap needs reserving too, or local dev would be left 20px short.
      const bottomOffset = appVersion === 'local' ? 20 : 0
      document.body.style.paddingBottom = `${el.offsetHeight + bottomOffset}px`
    }
    applyPadding()

    const observer = new ResizeObserver(applyPadding)
    observer.observe(el)
    return () => {
      observer.disconnect()
      document.body.style.paddingBottom = ''
    }
  }, [signInCheck.hasRequiredClaims])

  if (!signInCheck.hasRequiredClaims) {
    return null
  }

  return (
    <Alert
      ref={barRef}
      type="warning"
      style={{
        // theme-ok: fixed warning bar, deliberately the same in both themes
        backgroundColor: '#fa541c',
        color: token.colorTextLightSolid,
        position: 'fixed',
        zIndex: 1000,
        bottom: appVersion === 'local' ? 20 : 0,
        left: 0,
        right: 0,
      }}
      icon={<UserSwitchOutlined style={{ color: token.colorTextLightSolid }} />}
      action={[
        <Button
          key="end"
          onClick={endImpersonation}
          type="ghost"
          loading={loading}
          style={{ color: token.colorTextLightSolid, marginRight: 70 }}
        >
          {t('frontend:impersonation.exit')}
        </Button>,
      ]}
      showIcon
      banner
      message={
        <>
          {t('frontend:impersonation.impersonating')}{' '}
          <strong>
            {signInCheck.user.displayName} ({signInCheck.user.email})
          </strong>{' '}
          {impersonatorName && (
            <>
              {t('frontend:impersonation.as')} {impersonatorName}
            </>
          )}
        </>
      }
    />
  )
}
